import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import SecondLevelCategory, {
  maxSubcategoriesWhenCollapsed,
} from './second-level-category';
import { Category } from './types';
import { trackCategoryClick } from './tracking';
import * as S from './styles';

const getVisibleSubCategoryCount = (category: Category) =>
  category.children
    ? Math.min(category.children.length, maxSubcategoriesWhenCollapsed)
    : 0;

const getTotalVisibleSubCategoryCount = (categories: Category[]) =>
  categories.reduce(
    (count, category) => count + getVisibleSubCategoryCount(category),
    0
  );

const getCategoryWeight = (categories: Category[]) =>
  categories.length * 3 + getTotalVisibleSubCategoryCount(categories);

const chunkCategoriesInColumns = (categories: Category[]) => {
  const totalCategoryWeight = getCategoryWeight(categories);
  const oneThirdOfCategoryWeight = totalCategoryWeight / 3;
  const remainingCategories = categories.slice();

  const firstColumn = [];
  while (getCategoryWeight(firstColumn) < oneThirdOfCategoryWeight) {
    const nextCategory = remainingCategories.shift();
    if (!nextCategory) {
      break;
    }
    firstColumn.push(nextCategory);
  }

  const secondColumn = [];
  while (getCategoryWeight(secondColumn) < oneThirdOfCategoryWeight) {
    const nextCategory = remainingCategories.shift();
    if (!nextCategory) {
      break;
    }
    secondColumn.push(nextCategory);
  }

  return [firstColumn, secondColumn, remainingCategories];
};

type Props = {
  category: Category;
  isOpen: boolean;
  isScrolledDown: boolean;
  onClickCategoryLink: (id: string) => void;
};

const TopLevelCategory = ({
  category,
  isOpen,
  isScrolledDown,
  onClickCategoryLink,
}: Props) => {
  const { id, name, children = [] } = category;

  const categoryColumns = useMemo(
    () =>
      chunkCategoriesInColumns(children).map(categories => ({
        key: categories.map(c => c.id).join(','),
        categories,
      })),
    [children]
  );

  const onClickCurrentCategoryLink = useCallback(() => {
    trackCategoryClick(category, 0);
    onClickCategoryLink(id);
  }, [id, category, onClickCategoryLink]);

  const topLevelLinkRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (isOpen && topLevelLinkRef.current) {
      topLevelLinkRef.current.focus();
    }
  }, [isOpen]);

  return (
    <S.CategoryView
      key={id}
      visible={isOpen}
      data-test="MegaMenuView"
      data-test-id={id}
    >
      <S.TopLevelHeading showDivider={isScrolledDown}>
        <S.TopLevelLink
          onClick={onClickCurrentCategoryLink}
          category={category}
          ref={topLevelLinkRef}
        >
          {name}
        </S.TopLevelLink>
      </S.TopLevelHeading>
      <S.SecondLevelMenu>
        {categoryColumns.map(({ key, categories }) => (
          <S.SecondLevelColumn key={key}>
            {categories.map(secondLevelCategory => (
              <SecondLevelCategory
                key={secondLevelCategory.id}
                category={secondLevelCategory}
                onClickCategoryLink={onClickCategoryLink}
              />
            ))}
          </S.SecondLevelColumn>
        ))}
      </S.SecondLevelMenu>
    </S.CategoryView>
  );
};

export default TopLevelCategory;
